import { useState } from "react";

import { SeletorEmpresaAuditavel } from "../auditoria/SeletorEmpresaAuditavel";
import {
  FiltrosTrilha as ControlesFiltros,
  type CampoFiltro,
} from "../auditoria/FiltrosTrilha";
import { TabelaTrilha } from "../auditoria/TabelaTrilha";
import { DetalheAuditoria } from "../auditoria/DetalheAuditoria";
import { useTrilhaAuditoria } from "../../lib/auditoria/hooks";
import { LIMITE_DEFAULT, type FiltrosTrilha } from "../../lib/auditoria/tipos";
import { ErroHttp } from "../../lib/http/erros";

/*
 * Página da trilha de auditoria (FE-5 CP-C — LEITURA).
 *
 * Orquestra: o seletor de empresa (`empresaId`), os filtros (`filtros`, com
 * `limit = LIMITE_DEFAULT` fixo e `offset` paginável), a tabela e o painel de
 * detalhe (`registroSelecionado`).
 *
 * PAGINAÇÃO POR OFFSET (a resposta é array PURO, sem total no corpo):
 *  - "Anterior" habilitada quando `offset > 0`;
 *  - "Próxima" habilitada quando a página veio CHEIA (`length === limit`) — sinal
 *    de que PODE haver mais; página não-cheia ⇒ é a última (desabilita).
 *  - avançar/voltar move o `offset` por `LIMITE_DEFAULT`.
 *
 * FILTROS (estado limpo): só promovemos um campo a filtro ATIVO quando ele é
 * não-vazio. Campo esvaziado é REMOVIDO do objeto — assim a queryKey não carrega
 * `acao=""`/`usuario_id=""` (a camada de `api` já omite vazios, mas mantemos o
 * estado limpo para não disparar refetch espúrio). Trocar QUALQUER filtro (ou a
 * empresa) RESETA o `offset` para 0 e LIMPA a seleção de detalhe.
 *
 * ESTADOS de erro (sinais tipados do `ErroHttp`): 503 → indisponível; demais
 * reusam a mensagem estável — espelha `Projecao.tsx`/`Conciliacao.tsx`.
 */

/** Estado inicial dos filtros: só `limit`/`offset` (opcionais ausentes). */
function filtrosIniciais(): FiltrosTrilha {
  return { limit: LIMITE_DEFAULT, offset: 0 };
}

export function Trilha(): JSX.Element {
  const [empresaId, setEmpresaId] = useState<string | null>(null);
  const [filtros, setFiltros] = useState<FiltrosTrilha>(filtrosIniciais);
  const [registroSelecionado, setRegistroSelecionado] = useState<string | null>(
    null,
  );

  const consulta = useTrilhaAuditoria(empresaId, filtros);

  function aoTrocarEmpresa(novoId: string): void {
    setEmpresaId(novoId);
    // Trocar empresa reinicia paginação e limpa seleção.
    setFiltros(filtrosIniciais());
    setRegistroSelecionado(null);
  }

  function aoMudarFiltro(campo: CampoFiltro, valor: string): void {
    setFiltros((atual) => {
      // Recompõe os filtros: opcional vazio é REMOVIDO (estado limpo); sempre
      // reseta o offset (nova consulta começa da primeira página).
      const proximo: FiltrosTrilha = { ...atual, offset: 0 };
      if (valor === "") {
        delete proximo[campo];
      } else {
        proximo[campo] = valor;
      }
      return proximo;
    });
    setRegistroSelecionado(null);
  }

  function aoLimpar(): void {
    setFiltros(filtrosIniciais());
    setRegistroSelecionado(null);
  }

  function aoSelecionar(registroId: string): void {
    setRegistroSelecionado(registroId);
  }

  function paginaAnterior(): void {
    setFiltros((atual) => ({
      ...atual,
      offset: Math.max(0, atual.offset - atual.limit),
    }));
    setRegistroSelecionado(null);
  }

  function proximaPagina(): void {
    setFiltros((atual) => ({ ...atual, offset: atual.offset + atual.limit }));
    setRegistroSelecionado(null);
  }

  return (
    <section className="flex flex-col gap-6">
      <h1 className="font-titulo text-2xl text-off-black">
        Trilha de auditoria
      </h1>

      <SeletorEmpresaAuditavel
        selecionada={empresaId}
        aoSelecionar={aoTrocarEmpresa}
      />

      {empresaId === null ? (
        <p className="font-corpo text-base text-off-black">
          Selecione uma empresa para ver a trilha de auditoria.
        </p>
      ) : (
        <>
          <ControlesFiltros
            acao={filtros.acao ?? ""}
            usuarioId={filtros.usuario_id ?? ""}
            dataDe={filtros.data_de ?? ""}
            dataAte={filtros.data_ate ?? ""}
            recursoPrefixo={filtros.recurso_prefixo ?? ""}
            aoMudarFiltro={aoMudarFiltro}
            aoLimpar={aoLimpar}
          />

          <CorpoTrilha
            consulta={consulta}
            empresaId={empresaId}
            filtros={filtros}
            registroSelecionado={registroSelecionado}
            aoSelecionar={aoSelecionar}
            paginaAnterior={paginaAnterior}
            proximaPagina={proximaPagina}
          />
        </>
      )}
    </section>
  );
}

interface CorpoTrilhaProps {
  consulta: ReturnType<typeof useTrilhaAuditoria>;
  empresaId: string;
  filtros: FiltrosTrilha;
  registroSelecionado: string | null;
  aoSelecionar: (registroId: string) => void;
  paginaAnterior: () => void;
  proximaPagina: () => void;
}

/**
 * Corpo da trilha: trata loading/erro, renderiza a tabela + a paginação e, quando
 * há registro selecionado, o painel de detalhe ao lado.
 */
function CorpoTrilha({
  consulta,
  empresaId,
  filtros,
  registroSelecionado,
  aoSelecionar,
  paginaAnterior,
  proximaPagina,
}: CorpoTrilhaProps): JSX.Element {
  if (consulta.isPending) {
    return (
      <p className="font-corpo text-base text-off-black">
        Carregando trilha…
      </p>
    );
  }

  if (consulta.isError) {
    return (
      <p role="alert" className="font-corpo text-base text-flame">
        {mensagemErro(consulta.error)}
      </p>
    );
  }

  const itens = consulta.data;
  // Paginação por offset (array puro, sem total): "Próxima" só quando a página
  // veio CHEIA (pode haver mais); "Anterior" só quando há offset para recuar.
  const temProxima = itens.length === filtros.limit;
  const temAnterior = filtros.offset > 0;

  return (
    <div className="flex flex-col gap-4 md:flex-row md:gap-8">
      <div className="flex flex-col gap-4 md:flex-1">
        <TabelaTrilha
          itens={itens}
          selecionado={registroSelecionado}
          aoSelecionar={aoSelecionar}
        />

        <div className="flex items-center gap-4">
          <button
            type="button"
            onClick={paginaAnterior}
            disabled={!temAnterior}
            className="rounded-md border border-platinum bg-platinum px-3 py-1 font-subtitulo text-off-black hover:text-flame disabled:opacity-60"
          >
            Anterior
          </button>
          <button
            type="button"
            onClick={proximaPagina}
            disabled={!temProxima}
            className="rounded-md border border-platinum bg-platinum px-3 py-1 font-subtitulo text-off-black hover:text-flame disabled:opacity-60"
          >
            Próxima
          </button>
        </div>
      </div>

      {registroSelecionado !== null && (
        <div className="md:flex-1">
          <DetalheAuditoria
            empresaId={empresaId}
            registroId={registroSelecionado}
          />
        </div>
      )}
    </div>
  );
}

/**
 * Traduz o erro da consulta numa mensagem estável. 503 → indisponível; demais
 * reusam a mensagem do `ErroHttp`. Erro não-`ErroHttp` → fallback genérico.
 * Espelha o `mensagemErro` da projeção (mesma semântica).
 */
function mensagemErro(erro: unknown): string {
  if (erro instanceof ErroHttp) {
    if (erro.moduloIndisponivel) {
      return "Módulo de auditoria indisponível no momento.";
    }
    return erro.message;
  }
  return "Não foi possível carregar a trilha de auditoria.";
}
